import React, { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Skeletons, SectionHead } from "@/components/Primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Save, Building2, BrainCircuit, Languages, ShieldCheck, HardDrive, Bell, Palette, Plug, Loader2 } from "lucide-react";

const Field = ({ label, children, hint }) => (
  <div className="space-y-2">
    <Label className="text-xs font-semibold text-slate-700">{label}</Label>
    {children}
    {hint && <p className="text-xs text-slate-400">{hint}</p>}
  </div>
);

const Toggle = ({ label, desc, checked, onChange, testId }) => (
  <div className="flex items-start justify-between gap-4 rounded-xl border border-slate-200/70 p-4">
    <div>
      <p className="text-sm font-medium text-slate-800">{label}</p>
      {desc && <p className="mt-0.5 text-xs text-slate-500">{desc}</p>}
    </div>
    <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testId} />
  </div>
);

export default function Settings() {
  const { can } = useAuth();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const editable = can(["super_admin", "org_admin"]);

  useEffect(() => { api.get("/settings").then(({ data }) => setS(data)).catch((e) => toast.error(errMsg(e))); }, []);

  const upd = (group, key, value) => setS((p) => ({ ...p, [group]: { ...p[group], [key]: value } }));

  const save = async () => {
    setBusy(true);
    try {
      const { id, ...rest } = s;
      void id;
      const { data } = await api.put("/settings", { settings: rest });
      setS(data);
      toast.success("Pengaturan disimpan");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (!s) return <Skeletons rows={3} className="h-32" />;

  return (
    <div className="space-y-7" data-testid="settings-page">
      <div className="flex flex-wrap items-end justify-between gap-5">
        <div>
          <p className="label-xs">Konfigurasi Platform</p>
          <h1 className="mt-2.5 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">Settings</h1>
          <p className="mt-2 text-sm text-slate-500">{editable ? "Kelola organisasi, model AI, keamanan, dan integrasi." : "Anda memiliki akses baca saja."}</p>
        </div>
        {editable && (
          <Button className="h-11 rounded-xl grad-brand text-white px-5" onClick={save} disabled={busy} data-testid="save-settings-btn">
            {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Save size={16} className="mr-2" />} Simpan Perubahan
          </Button>
        )}
      </div>

      <Tabs defaultValue="org">
        <TabsList className="rounded-xl bg-slate-100/80 p-1 flex-wrap h-auto" data-testid="settings-tabs">
          <TabsTrigger value="org" className="rounded-lg" data-testid="settings-tab-org"><Building2 size={14} className="mr-1.5" />Organisasi</TabsTrigger>
          <TabsTrigger value="ai" className="rounded-lg" data-testid="settings-tab-ai"><BrainCircuit size={14} className="mr-1.5" />AI Models</TabsTrigger>
          <TabsTrigger value="lang" className="rounded-lg" data-testid="settings-tab-lang"><Languages size={14} className="mr-1.5" />Bahasa</TabsTrigger>
          <TabsTrigger value="sec" className="rounded-lg" data-testid="settings-tab-sec"><ShieldCheck size={14} className="mr-1.5" />Keamanan</TabsTrigger>
          <TabsTrigger value="storage" className="rounded-lg" data-testid="settings-tab-storage"><HardDrive size={14} className="mr-1.5" />Storage</TabsTrigger>
          <TabsTrigger value="notif" className="rounded-lg" data-testid="settings-tab-notif"><Bell size={14} className="mr-1.5" />Notifikasi</TabsTrigger>
          <TabsTrigger value="brand" className="rounded-lg" data-testid="settings-tab-brand"><Palette size={14} className="mr-1.5" />Branding</TabsTrigger>
          <TabsTrigger value="api" className="rounded-lg" data-testid="settings-tab-api"><Plug size={14} className="mr-1.5" />API</TabsTrigger>
        </TabsList>

        <TabsContent value="org" className="mt-6">
          <div className="clay p-7 grid gap-5 sm:grid-cols-2 max-w-3xl">
            <Field label="Nama Organisasi">
              <Input value={s.organization?.name || ""} disabled={!editable} onChange={(e) => upd("organization", "name", e.target.value)} className="h-11 rounded-xl" data-testid="setting-org-name" />
            </Field>
            <Field label="Zona Waktu">
              <Input value={s.organization?.timezone || ""} disabled={!editable} onChange={(e) => upd("organization", "timezone", e.target.value)} className="h-11 rounded-xl" data-testid="setting-org-tz" />
            </Field>
            <Field label="URL Logo" hint="Digunakan pada laporan export">
              <Input value={s.organization?.logo_url || ""} disabled={!editable} onChange={(e) => upd("organization", "logo_url", e.target.value)} className="h-11 rounded-xl" data-testid="setting-org-logo" />
            </Field>
          </div>
        </TabsContent>

        <TabsContent value="ai" className="mt-6">
          <div className="clay p-7 max-w-3xl">
            <SectionHead title="AI Engine" sub="Model aktif pada pipeline pemrosesan rapat" />
            <div className="grid gap-5 sm:grid-cols-2">
              {[["stt", "Speech To Text"], ["diarization", "Speaker Diarization"], ["biometrics", "Voice Biometrics"], ["llm", "LLM Summary"], ["punctuation", "Punctuation"]].map(([k, l]) => (
                <Field key={k} label={l}>
                  <Input value={s.ai_models?.[k] || ""} disabled={!editable} onChange={(e) => upd("ai_models", k, e.target.value)} className="h-11 rounded-xl font-mono text-xs" data-testid={`setting-ai-${k}`} />
                </Field>
              ))}
            </div>
            <p className="mt-6 rounded-xl bg-amber-50 border border-amber-200 p-4 text-xs leading-relaxed text-amber-800">
              Catatan: diarization &amp; voice biometrics dijalankan dalam mode <b>SIMULASI</b> pada environment ini. Engine Pyannote/SpeechBrain/NeMo asli aktif pada deployment on-premise ber-GPU.
            </p>
          </div>
        </TabsContent>

        <TabsContent value="lang" className="mt-6">
          <div className="clay p-7 space-y-5 max-w-2xl">
            <Field label="Bahasa Default">
              <Input value={s.language?.default || ""} disabled={!editable} onChange={(e) => upd("language", "default", e.target.value)} className="h-11 rounded-xl" data-testid="setting-lang-default" />
            </Field>
            <Toggle label="Auto Language Detection" desc="Deteksi bahasa otomatis pada transkripsi" checked={s.language?.auto_detect}
              onChange={(v) => editable && upd("language", "auto_detect", v)} testId="setting-lang-autodetect" />
          </div>
        </TabsContent>

        <TabsContent value="sec" className="mt-6">
          <div className="clay p-7 space-y-4 max-w-2xl">
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Enkripsi Data"><Input value={s.security?.encryption || ""} disabled className="h-11 rounded-xl font-mono text-xs" data-testid="setting-sec-encryption" /></Field>
              <Field label="Session Timeout (menit)">
                <Input type="number" value={s.security?.session_timeout_minutes || 30} disabled={!editable}
                  onChange={(e) => upd("security", "session_timeout_minutes", Number(e.target.value))} className="h-11 rounded-xl" data-testid="setting-sec-timeout" />
              </Field>
            </div>
            <Toggle label="TLS / SSL" desc="Enkripsi transport wajib" checked={s.security?.tls} onChange={(v) => editable && upd("security", "tls", v)} testId="setting-sec-tls" />
            <Toggle label="Watermark Dokumen" desc="Sisipkan identitas pengguna pada semua export" checked={s.security?.watermark} onChange={(v) => editable && upd("security", "watermark", v)} testId="setting-sec-watermark" />
            <Toggle label="Audit Trail" desc="Catat semua aktivitas pengguna" checked={s.security?.audit_trail} onChange={(v) => editable && upd("security", "audit_trail", v)} testId="setting-sec-audit" />
            <Toggle label="On-Premise Mode" desc="Tanpa ketergantungan cloud publik" checked={s.security?.on_premise} onChange={(v) => editable && upd("security", "on_premise", v)} testId="setting-sec-onprem" />
          </div>
        </TabsContent>

        <TabsContent value="storage" className="mt-6">
          <div className="clay p-7 grid gap-5 sm:grid-cols-3 max-w-3xl">
            <Field label="Provider"><Input value={s.storage?.provider || ""} disabled={!editable} onChange={(e) => upd("storage", "provider", e.target.value)} className="h-11 rounded-xl" data-testid="setting-storage-provider" /></Field>
            <Field label="Retensi (hari)"><Input type="number" value={s.storage?.retention_days || 365} disabled={!editable} onChange={(e) => upd("storage", "retention_days", Number(e.target.value))} className="h-11 rounded-xl" data-testid="setting-storage-retention" /></Field>
            <Field label="Kuota (GB)"><Input type="number" value={s.storage?.quota_gb || 500} disabled={!editable} onChange={(e) => upd("storage", "quota_gb", Number(e.target.value))} className="h-11 rounded-xl" data-testid="setting-storage-quota" /></Field>
          </div>
        </TabsContent>

        <TabsContent value="notif" className="mt-6">
          <div className="clay p-7 space-y-4 max-w-2xl">
            <Toggle label="Email Summary" desc="Kirim ringkasan rapat via email" checked={s.notification?.email_summary} onChange={(v) => editable && upd("notification", "email_summary", v)} testId="setting-notif-email" />
            <Toggle label="Notifikasi In-App" desc="Tampilkan notifikasi pada aplikasi" checked={s.notification?.in_app} onChange={(v) => editable && upd("notification", "in_app", v)} testId="setting-notif-inapp" />
            <Toggle label="WhatsApp Notification" desc="Roadmap — belum aktif" checked={s.notification?.whatsapp} onChange={(v) => editable && upd("notification", "whatsapp", v)} testId="setting-notif-wa" />
          </div>
        </TabsContent>

        <TabsContent value="brand" className="mt-6">
          <div className="clay p-7 grid gap-5 sm:grid-cols-2 max-w-2xl">
            <Field label="Warna Primary"><Input value={s.branding?.primary_color || ""} disabled={!editable} onChange={(e) => upd("branding", "primary_color", e.target.value)} className="h-11 rounded-xl font-mono text-xs" data-testid="setting-brand-primary" /></Field>
            <Field label="Warna Accent"><Input value={s.branding?.accent_color || ""} disabled={!editable} onChange={(e) => upd("branding", "accent_color", e.target.value)} className="h-11 rounded-xl font-mono text-xs" data-testid="setting-brand-accent" /></Field>
            <div className="sm:col-span-2 flex gap-3">
              <div className="h-16 flex-1 rounded-xl" style={{ background: s.branding?.primary_color }} />
              <div className="h-16 flex-1 rounded-xl" style={{ background: s.branding?.accent_color }} />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="api" className="mt-6">
          <div className="clay p-7 space-y-5 max-w-2xl">
            <Toggle label="REST API" desc="Aktifkan endpoint integrasi eksternal" checked={s.api?.rest_enabled} onChange={(v) => editable && upd("api", "rest_enabled", v)} testId="setting-api-rest" />
            <Field label="Webhook URL" hint="Dipanggil setelah AI Summary selesai">
              <Input value={s.api?.webhook_url || ""} disabled={!editable} onChange={(e) => upd("api", "webhook_url", e.target.value)} className="h-11 rounded-xl font-mono text-xs" placeholder="https://…" data-testid="setting-api-webhook" />
            </Field>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
